import clsx from "clsx";
import { useMemo } from "react";

import Card from "~/components/Card/Card";
import HorizontalStatDetail from "~/components/HorizontalStats/components/HorizontalStatDetail/HorizontalStatDetail";
import type { HorizontalStat } from "~/components/HorizontalStats/horizontalStatsTypes.ts";
import OverflowPercentageChart from "~/components/OverflowPercentageChart/OverflowPercentageChart.tsx";
import Tooltip from "~/components/Tooltip";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	budgetCentsFromQuotas?: number;
	budgetCentsPlanned: number;
	budgetCentsTracked: number;
	manDaysPlanned: number;
	manDaysTracked: number;
	showBudget?: boolean;
}

const phaseTooltipText = "Das Gesamtbudget der Phase errechnet sich aus der Summe der Budgets aller bestätigten Bestellungen der Phase, einschließlich der Kontingentanteile, die das Budget einer Bestellung erhöhen.\nSind keine bestätigten Bestellungen vorhanden, wird hier der Planwert der Phase angezeigt.";

const PhaseBudgetStatsSection = ({
	budgetCentsFromQuotas = 0,
	budgetCentsPlanned,
	budgetCentsTracked,
	manDaysPlanned,
	manDaysTracked,
	showBudget = true,
}: Props) => {

	const useCurrencyValues = showBudget;
	const plannedValue = useCurrencyValues ? budgetCentsPlanned : manDaysPlanned;
	const trackedValue = useCurrencyValues ? budgetCentsTracked : manDaysTracked;
	const percentageTracked = (trackedValue / plannedValue) * 100;
	const rest = plannedValue - trackedValue;

	const plannedValueFormatted = useCurrencyValues ? formatCentsToCurrency(plannedValue) : plannedValue.toFixed(0) + " PT";
	const trackedValueFormatted = useCurrencyValues ? formatCentsToCurrency(trackedValue) : trackedValue > 0 && trackedValue < 1 ? "<" : "" + +trackedValue.toFixed(0) + " PT";
	const restFormatted = useCurrencyValues ? formatCentsToCurrency(rest) : rest.toFixed(0) + " PT";
	const percentageTrackedFormatted = percentageTracked === Infinity ? "n/a" : percentageTracked.toFixed(0) + "%";

	const stats: HorizontalStat[] = useMemo(() => {
		return [
			{
				label: "Verfügt",
				value: trackedValueFormatted,
			},
			{
				label: "Rest",
				value: restFormatted,
				highlight: rest < 0,
			},
		];
	}, [rest, restFormatted, trackedValueFormatted]);

	return <Card>
		<div className="flex h-full flex-col justify-center">
			<div className="flex items-center">
				<OverflowPercentageChart percentage={percentageTracked === Infinity ? 0 : percentageTracked}
										 size={90}
										 thickness={0.4}>
					<div className="text-center text-sm font-bold">
						{percentageTrackedFormatted}
					</div>
				</OverflowPercentageChart>

				<div className={clsx("flex h-full grow flex-col justify-center gap-y-2")}>
					<div className="flex items-center justify-between">
						<div className="relative flex grow flex-col items-center justify-center px-2">
							<div className="flex shrink-0 px-1 text-xs font-medium text-gray-500">
								<span className="mr-1 whitespace-nowrap">Gesamtbudget Phase</span>
								<Tooltip text={phaseTooltipText}
										 className="whitespace-pre-wrap"
										 width={280}
								>
									<div className="flex size-4 cursor-default select-none items-center justify-center rounded-full bg-gray-300 text-xs text-white">?</div>
								</Tooltip>
							</div>
							<div className="text-lg font-bold text-gray-700">
								{plannedValueFormatted}
							</div>
							{/* Positioned below the value, so the total stays aligned with the other stats. */}
							{useCurrencyValues && budgetCentsFromQuotas > 0 && <div className="absolute inset-x-0 top-full whitespace-nowrap text-center text-xs text-gray-500">
								davon {formatCentsToCurrency(budgetCentsFromQuotas)} aus Kontingenten
							</div>}
						</div>
						{stats.map((stat, index) => {
							return <HorizontalStatDetail key={index}
														 label={stat.label}
														 value={stat.value}
														 isHighlighted={stat.isHighlighted} />;
						})}
					</div>
				</div>
			</div>
		</div>
	</Card>;
};

export default PhaseBudgetStatsSection;